<template>
  <div class="tutorial-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="tutorial-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">WhatsApp Integration Tutorial</h1>
              <p class="page-subtitle">Complete step-by-step guide to connecting your WhatsApp Business API</p>
            </div>
            <div class="language-switcher">
              <button
                @click="switchLanguage('en')"
                :class="{ active: currentLang === 'en' }"
                class="lang-btn"
              >
                English
              </button>
              <button
                @click="switchLanguage('ar')"
                :class="{ active: currentLang === 'ar' }"
                class="lang-btn"
              >
                العربية
              </button>
            </div>
          </div>

          <!-- Progress Bar -->
          <div class="progress-section">
            <div class="progress-header">
              <div class="progress-info">
                <span class="progress-label">Progress:</span>
                <span class="progress-percentage">{{ progress }}%</span>
              </div>
              <div class="step-counter">
                Step {{ currentStep + 1 }} of {{ allSteps.length }}
              </div>
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar" :style="{ width: progress + '%' }">
                <div class="progress-bar-glow"></div>
              </div>
            </div>
            <div class="progress-milestones">
              <div
                v-for="(step, index) in allSteps"
                :key="index"
                class="milestone"
                :class="{
                  active: index === currentStep,
                  completed: completedSteps.includes(index)
                }"
                @click="goToStep(index)"
                :title="`Step ${index + 1}: ${step.id}`"
              >
                <div class="milestone-dot">
                  <svg v-if="completedSteps.includes(index)" width="10" height="10" viewBox="0 0 24 24" fill="white">
                    <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Overview Stats (Always Visible) -->
          <div class="stats-overview">
            <div class="stat-card">
              <div class="stat-icon">📱</div>
              <div class="stat-content">
                <div class="stat-number">3</div>
                <div class="stat-label">{{ translations[currentLang].mainSteps }}</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon">✅</div>
              <div class="stat-content">
                <div class="stat-number">100%</div>
                <div class="stat-label">{{ translations[currentLang].freeToUse }}</div>
              </div>
            </div>
          </div>

          <!-- Step Content Area -->
          <div class="step-content-wrapper">
            <transition name="fade" mode="out-in">
              <!-- Introduction Step -->
              <div v-if="currentStepData.type === 'intro'" key="intro" class="step-card">
                <div class="step-badge">Introduction</div>
                <h2 class="card-title">{{ translations[currentLang].welcome }}</h2>
                <p class="intro-text" v-html="translations[currentLang].introText"></p>
                <div class="accomplishments">
                  <h3>{{ translations[currentLang].whatYouWillAccomplish }}</h3>
                  <ul class="checklist">
                    <li v-for="(item, index) in translations[currentLang].accomplishList" :key="index">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="check-icon">
                        <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                      </svg>
                      <span>{{ item }}</span>
                    </li>
                  </ul>
                </div>
                <div class="info-note">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <div>
                    <strong>{{ translations[currentLang].important }}:</strong>
                    {{ translations[currentLang].importantNote }}
                  </div>
                </div>
                <div class="how-it-works-section">
                  <h3 class="section-subtitle">{{ translations[currentLang].howThisWorks }}</h3>
                  <div class="architecture-diagram">
                    <div class="arch-item">
                      <div class="arch-icon">🏢</div>
                      <div class="arch-label">{{ translations[currentLang].yourMetaApp }}</div>
                    </div>
                    <div class="arch-arrow">→</div>
                    <div class="arch-item">
                      <div class="arch-icon">📱</div>
                      <div class="arch-label">{{ translations[currentLang].yourWhatsApp }}</div>
                    </div>
                    <div class="arch-arrow">→</div>
                    <div class="arch-item">
                      <div class="arch-icon">🤖</div>
                      <div class="arch-label">QUALI-D AI</div>
                    </div>
                    <div class="arch-arrow">→</div>
                    <div class="arch-item">
                      <div class="arch-icon">👥</div>
                      <div class="arch-label">{{ translations[currentLang].yourCustomers }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 1.1: Access Meta -->
              <div v-else-if="currentStepData.id === '1.1'" key="1.1" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.1</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_1 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step1_1Content }}</p>
                <div class="url-box">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.36,14C16.44,13.34 16.5,12.68 16.5,12C16.5,11.32 16.44,10.66 16.36,10H19.74C19.9,10.64 20,11.31 20,12C20,12.69 19.9,13.36 19.74,14M14.59,19.56C15.19,18.45 15.65,17.25 15.97,16H18.92C17.96,17.65 16.43,18.93 14.59,19.56M14.34,14H9.66C9.56,13.34 9.5,12.68 9.5,12C9.5,11.32 9.56,10.65 9.66,10H14.34C14.43,10.65 14.5,11.32 14.5,12C14.5,12.68 14.43,13.34 14.34,14M12,19.96C11.17,18.76 10.5,17.43 10.09,16H13.91C13.5,17.43 12.83,18.76 12,19.96M8,8H5.08C6.03,6.34 7.57,5.06 9.4,4.44C8.8,5.55 8.35,6.75 8,8M5.08,16H8C8.35,17.25 8.8,18.45 9.4,19.56C7.57,18.93 6.03,17.65 5.08,16M4.26,14C4.1,13.36 4,12.69 4,12C4,11.31 4.1,10.64 4.26,10H7.64C7.56,10.66 7.5,11.32 7.5,12C7.5,12.68 7.56,13.34 7.64,14M12,4.03C12.83,5.23 13.5,6.57 13.91,8H10.09C10.5,6.57 11.17,5.23 12,4.03M18.92,8H15.97C15.65,6.75 15.19,5.55 14.59,4.44C16.43,5.07 17.96,6.34 18.92,8M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <a href="https://developers.facebook.com/" target="_blank">developers.facebook.com</a>
                </div>
                <div class="note-box info">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <span>{{ translations[currentLang].step1_1Note }}</span>
                </div>
              </div>

              <!-- Step 1.2: Create New App -->
              <div v-else-if="currentStepData.id === '1.2'" key="1.2" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.2</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_2 }}</h2>
                </div>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step1_2List" :key="index" v-html="item"></li>
                </ol>
                <div class="tutorial-screenshot" @click="openLightbox(require('@/assets/tutorials/whatsapp/create-app-business.png'), 'Create App page with Business type selected')">
                  <img src="@/assets/tutorials/whatsapp/create-app-business.png" alt="Create App page with Business type selected" />
                  <div class="screenshot-overlay">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                      <path d="M15,3L21,3V9H19V6.41L11.91,13.5L10.5,12.09L17.59,5H15V3M5,5C3.89,5 3,5.89 3,7V19A2,2 0 0,0 5,21H17A2,2 0 0,0 19,19V13H17V19H5V7H11V5H5Z"/>
                    </svg>
                  </div>
                </div>
              </div>

              <!-- Step 1.3: Configure App Details -->
              <div v-else-if="currentStepData.id === '1.3'" key="1.3" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.3</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_3 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step1_3Content }}</p>
                <ul class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step1_3List" :key="index" v-html="item"></li>
                </ul>
                <div class="note-box warning">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
                  </svg>
                  <span>{{ translations[currentLang].step1_3Note }}</span>
                </div>
              </div>

              <!-- Step 1.4: Select Use Case -->
              <div v-else-if="currentStepData.id === '1.4'" key="1.4" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.4</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_4 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step1_4Content }}</p>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step1_4List" :key="index" v-html="item"></li>
                </ol>
                <div class="tutorial-screenshot" @click="openLightbox(require('@/assets/tutorials/whatsapp/use-case-selection.png'), 'Use case selection with WhatsApp option')">
                  <img src="@/assets/tutorials/whatsapp/use-case-selection.png" alt="Use case selection with WhatsApp option" />
                  <div class="screenshot-overlay">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                      <path d="M15,3L21,3V9H19V6.41L11.91,13.5L10.5,12.09L17.59,5H15V3M5,5C3.89,5 3,5.89 3,7V19A2,2 0 0,0 5,21H17A2,2 0 0,0 19,19V13H17V19H5V7H11V5H5Z"/>
                    </svg>
                  </div>
                </div>
                <div class="note-box info">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <span>{{ translations[currentLang].step1_4Note }}</span>
                </div>
              </div>

              <!-- Step 1.5: Choose Business Portfolio -->
              <div v-else-if="currentStepData.id === '1.5'" key="1.5" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.5</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_5 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step1_5Content }}</p>
                <ul class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step1_5List" :key="index" v-html="item"></li>
                </ul>
                <div class="note-box info">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <span>{{ translations[currentLang].step1_5Note }}</span>
                </div>
              </div>

              <!-- Step 1.6: Review and Confirm -->
              <div v-else-if="currentStepData.id === '1.6'" key="1.6" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.6</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_6 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step1_6Content }}</p>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step1_6List" :key="index" v-html="item"></li>
                </ol>
                <div class="note-box success">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                  </svg>
                  <span>{{ translations[currentLang].step1_6Note }}</span>
                </div>
              </div>

              <!-- Step 1.7: Customize WhatsApp Use Case -->
              <div v-else-if="currentStepData.id === '1.7'" key="1.7" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.7</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_7 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step1_7Content }}</p>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step1_7List" :key="index" v-html="item"></li>
                </ol>
                <div class="note-box info">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <span>{{ translations[currentLang].step1_7Note }}</span>
                </div>
              </div>

              <!-- Step 1.8: Accept Terms and Select Business Portfolio -->
              <div v-else-if="currentStepData.id === '1.8'" key="1.8" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.8</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_8 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step1_8Content }}</p>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step1_8List" :key="index" v-html="item"></li>
                </ol>
                <div class="note-box warning">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
                  </svg>
                  <span>{{ translations[currentLang].step1_8Note }}</span>
                </div>
              </div>

              <!-- Step 1.9: Start Using the API -->
              <div v-else-if="currentStepData.id === '1.9'" key="1.9" class="step-card">
                <div class="step-badge">Step 1 of 3: Create Meta App</div>
                <div class="step-header-inline">
                  <div class="step-number">1.9</div>
                  <h2 class="step-title">{{ translations[currentLang].step1_9 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step1_9Content }}</p>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step1_9List" :key="index" v-html="item"></li>
                </ol>
                <div class="note-box success">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                  </svg>
                  <span>{{ translations[currentLang].step1_9Note }}</span>
                </div>
              </div>

              <!-- Step 2.1: Copy Phone Number ID -->
              <div v-else-if="currentStepData.id === '2.1'" key="2.1" class="step-card">
                <div class="step-badge">Step 2 of 3: {{ translations[currentLang].step2Title }}</div>
                <div class="step-header-inline">
                  <div class="step-number">2.1</div>
                  <h2 class="step-title">{{ translations[currentLang].step2_1 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step2_1Content }}</p>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step2_1List" :key="index" v-html="item"></li>
                </ol>
                <div class="tutorial-screenshot" @click="openLightbox(require('@/assets/tutorials/whatsapp/api-setup-phone-number-id.png'), 'API Setup page showing Phone Number ID')">
                  <img src="@/assets/tutorials/whatsapp/api-setup-phone-number-id.png" alt="API Setup page showing Phone Number ID" />
                  <div class="screenshot-overlay">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                      <path d="M15,3L21,3V9H19V6.41L11.91,13.5L10.5,12.09L17.59,5H15V3M5,5C3.89,5 3,5.89 3,7V19A2,2 0 0,0 5,21H17A2,2 0 0,0 19,19V13H17V19H5V7H11V5H5Z"/>
                    </svg>
                  </div>
                </div>
                <div class="note-box warning">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
                  </svg>
                  <span>{{ translations[currentLang].step2_1Warning }}</span>
                </div>
                <div class="note-box info">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <span>{{ translations[currentLang].step2_1Note }}</span>
                </div>
              </div>

              <!-- Step 3.1: Create System User -->
              <div v-else-if="currentStepData.id === '3.1'" key="3.1" class="step-card">
                <div class="step-badge">Step 3 of 3: Generate Access Token</div>
                <div class="step-header-inline">
                  <div class="step-number">3.1</div>
                  <h2 class="step-title">{{ translations[currentLang].step3_1 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step3_1Content }}</p>
                <div class="url-box">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.36,14C16.44,13.34 16.5,12.68 16.5,12C16.5,11.32 16.44,10.66 16.36,10H19.74C19.9,10.64 20,11.31 20,12C20,12.69 19.9,13.36 19.74,14M14.59,19.56C15.19,18.45 15.65,17.25 15.97,16H18.92C17.96,17.65 16.43,18.93 14.59,19.56M14.34,14H9.66C9.56,13.34 9.5,12.68 9.5,12C9.5,11.32 9.56,10.65 9.66,10H14.34C14.43,10.65 14.5,11.32 14.5,12C14.5,12.68 14.43,13.34 14.34,14M12,19.96C11.17,18.76 10.5,17.43 10.09,16H13.91C13.5,17.43 12.83,18.76 12,19.96M8,8H5.08C6.03,6.34 7.57,5.06 9.4,4.44C8.8,5.55 8.35,6.75 8,8M5.08,16H8C8.35,17.25 8.8,18.45 9.4,19.56C7.57,18.93 6.03,17.65 5.08,16M4.26,14C4.1,13.36 4,12.69 4,12C4,11.31 4.1,10.64 4.26,10H7.64C7.56,10.66 7.5,11.32 7.5,12C7.5,12.68 7.56,13.34 7.64,14M12,4.03C12.83,5.23 13.5,6.57 13.91,8H10.09C10.5,6.57 11.17,5.23 12,4.03M18.92,8H15.97C15.65,6.75 15.19,5.55 14.59,4.44C16.43,5.07 17.96,6.34 18.92,8M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <a href="https://business.facebook.com/" target="_blank" rel="noopener noreferrer" class="url-link">
                    https://business.facebook.com/
                  </a>
                </div>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step3_1List" :key="index" v-html="item"></li>
                </ol>
              </div>

              <!-- Step 3.2: Assign Assets -->
              <div v-else-if="currentStepData.id === '3.2'" key="3.2" class="step-card">
                <div class="step-badge">Step 3 of 3: Generate Access Token</div>
                <div class="step-header-inline">
                  <div class="step-number">3.2</div>
                  <h2 class="step-title">{{ translations[currentLang].step3_2 }}</h2>
                </div>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step3_2List" :key="index" v-html="item"></li>
                </ol>
              </div>

              <!-- Step 3.3: Generate Token -->
              <div v-else-if="currentStepData.id === '3.3'" key="3.3" class="step-card">
                <div class="step-badge">Step 3 of 3: Generate Access Token</div>
                <div class="step-header-inline">
                  <div class="step-number">3.3</div>
                  <h2 class="step-title">{{ translations[currentLang].step3_3 }}</h2>
                </div>
                <ol class="instruction-list">
                  <li v-for="(item, index) in translations[currentLang].step3_3List" :key="index" v-html="item"></li>
                </ol>
                <div class="note-box danger">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1Z"/>
                  </svg>
                  <div>
                    <strong>{{ translations[currentLang].securityWarning }}:</strong>
                    {{ translations[currentLang].step3_3Warning }}
                  </div>
                </div>
              </div>

              <!-- Step 4.1: Save Credentials -->
              <div v-else-if="currentStepData.id === '4.1'" key="4.1" class="step-card">
                <div class="step-badge">Step 4 of 4: {{ translations[currentLang].step4Title }}</div>
                <div class="step-header-inline">
                  <div class="step-number">4.1</div>
                  <h2 class="step-title">{{ translations[currentLang].step4_1 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step4_1Content }}</p>

                <!-- WhatsApp Settings Form -->
                <form @submit.prevent="saveWhatsAppSettings" class="whatsapp-settings-form">
                  <div class="form-group">
                    <label class="form-label">{{ translations[currentLang].whatsappTokenLabel }}</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <circle cx="12" cy="7" r="4"/>
                        <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
                      </svg>
                      <input
                        v-model="whatsappSettings.whatsappToken"
                        :type="showToken ? 'text' : 'password'"
                        :placeholder="translations[currentLang].whatsappTokenPlaceholder"
                        class="form-input"
                        required
                      />
                      <button
                        type="button"
                        @click="showToken = !showToken"
                        class="toggle-password-btn"
                      >
                        <svg v-if="!showToken" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                          <line x1="2" y1="2" x2="22" y2="22"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="form-label">{{ translations[currentLang].whatsappPhoneIdLabel }}</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="18" x2="12.01" y2="18"/>
                      </svg>
                      <input
                        v-model="whatsappSettings.whatsappPhoneId"
                        type="text"
                        :placeholder="translations[currentLang].whatsappPhoneIdPlaceholder"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    class="save-credentials-btn"
                    :disabled="settingsLoading"
                    :class="{ loading: settingsLoading, success: settingsSaved }"
                  >
                    <svg v-if="settingsSaved" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                    </svg>
                    <span v-if="settingsLoading">{{ translations[currentLang].savingCredentials }}</span>
                    <span v-else-if="settingsSaved">{{ translations[currentLang].credentialsSaved }}</span>
                    <span v-else>{{ translations[currentLang].saveCredentialsBtn }}</span>
                  </button>

                  <div v-if="settingsError" class="note-box danger">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z"/>
                    </svg>
                    <span>{{ settingsError }}</span>
                  </div>
                </form>

                <!-- Webhook Configuration -->
                <div class="webhook-section">
                  <h3 class="webhook-title">{{ translations[currentLang].webhookConfigTitle }}</h3>
                  <p class="webhook-desc">{{ translations[currentLang].webhookConfigDesc }}</p>

                  <!-- Webhook URL -->
                  <div class="webhook-field">
                    <label class="webhook-label">{{ translations[currentLang].webhookUrlLabel }}</label>
                    <div class="webhook-value-container">
                      <input
                        type="text"
                        :value="webhookUrl || translations[currentLang].loading"
                        readonly
                        class="webhook-input"
                        :class="{ loading: webhookLoading }"
                      />
                      <button
                        @click="copyWebhookUrl"
                        class="copy-btn"
                        :disabled="webhookLoading || !webhookUrl"
                        :title="translations[currentLang].copyWebhookUrl"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Verify Token -->
                  <div class="webhook-field">
                    <label class="webhook-label">{{ translations[currentLang].verifyTokenLabel }}</label>
                    <div class="webhook-value-container">
                      <input
                        type="text"
                        value="secret@2024"
                        readonly
                        class="webhook-input"
                      />
                      <button
                        @click="copyVerifyToken"
                        class="copy-btn"
                        :title="translations[currentLang].copyVerifyToken"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div v-if="webhookCopied" class="note-box success">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                    </svg>
                    <span>{{ webhookCopied }}</span>
                  </div>
                </div>

                <div class="note-box info">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <span>{{ translations[currentLang].step4_1Note }}</span>
                </div>

                <div v-if="!credentialsValidated && (whatsappSettings.whatsappToken || whatsappSettings.whatsappPhoneId)" class="note-box warning" style="margin-top: 16px;">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
                  </svg>
                  <span>{{ currentLang === 'en' ? 'You must save your credentials before proceeding to the next step.' : 'يجب عليك حفظ بيانات الاعتماد قبل المتابعة إلى الخطوة التالية.' }}</span>
                </div>

                <div v-if="credentialsValidated && !settingsSaved" class="note-box success" style="margin-top: 16px;">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                  </svg>
                  <span>{{ currentLang === 'en' ? 'Credentials saved successfully! You can now proceed to the next step.' : 'تم حفظ بيانات الاعتماد بنجاح! يمكنك الآن المتابعة إلى الخطوة التالية.' }}</span>
                </div>
              </div>

              <!-- Step 4.2: Initialize Connection -->
              <div v-else-if="currentStepData.id === '4.2'" key="4.2" class="step-card">
                <div class="step-badge">Step 4 of 4: {{ translations[currentLang].step4Title }}</div>
                <div class="step-header-inline">
                  <div class="step-number">4.2</div>
                  <h2 class="step-title">{{ translations[currentLang].step4_2 }}</h2>
                </div>
                <p class="step-desc">{{ translations[currentLang].step4_2Content }}</p>
                <p class="step-desc">{{ translations[currentLang].step4_2Instructions }}</p>
                <ul class="checklist">
                  <li v-for="(item, index) in translations[currentLang].step4_2List" :key="index">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="check-icon">
                      <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                    </svg>
                    <span>{{ item }}</span>
                  </li>
                </ul>

                <!-- WhatsApp Connection Status Card (Mini Hero Card) -->
                <div class="whatsapp-connection-card">
                  <div class="connection-visual">
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                      alt="WhatsApp"
                      class="whatsapp-icon"
                    />
                    <div v-if="whatsappStatus.status === 'Connected'" class="status-glow active"></div>
                  </div>

                  <div class="connection-info">
                    <div class="status-label-row">
                      <span class="status-label-text">{{ translations[currentLang].currentStatus }}:</span>
                      <span class="status-value" :class="{
                        connected: whatsappStatus.status === 'Connected',
                        connecting: whatsappStatus.status === 'Connecting',
                        disconnected: whatsappStatus.status !== 'Connected' && whatsappStatus.status !== 'Connecting'
                      }">
                        {{ whatsappStatus.status || 'Unknown' }}
                      </span>
                    </div>
                    <div v-if="whatsappStatus.uniqueSessionId" class="session-info-row">
                      <small class="session-id-text">ID: {{ whatsappStatus.uniqueSessionId.substring(0, 12) }}...</small>
                    </div>
                  </div>

                  <div class="connection-actions">
                    <button
                      v-if="whatsappStatus.status !== 'Connected'"
                      @click="initializeWhatsAppConnection"
                      :disabled="connectionLoading"
                      class="initialize-connection-btn"
                      :class="{ loading: connectionLoading }"
                    >
                      <span class="btn-icon">🚀</span>
                      <span v-if="connectionLoading">{{ translations[currentLang].initializing }}</span>
                      <span v-else>{{ translations[currentLang].initializeBtn }}</span>
                    </button>

                    <button
                      @click="recheckWhatsAppConnection"
                      :disabled="connectionLoading"
                      class="recheck-connection-btn"
                    >
                      <span class="btn-icon">🔄</span>
                      <span v-if="connectionLoading">{{ translations[currentLang].rechecking }}</span>
                      <span v-else>{{ translations[currentLang].recheckBtn }}</span>
                    </button>
                  </div>
                </div>

                <!-- Connection Status Messages -->
                <div v-if="connectionSuccess" class="note-box success">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                  </svg>
                  <span>{{ translations[currentLang].connectionSuccess }}</span>
                </div>

                <div v-if="whatsappStatus.status === 'Connecting'" class="note-box info">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <span>{{ translations[currentLang].connectionPending }}</span>
                </div>

                <div v-if="connectionError" class="note-box danger">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z"/>
                  </svg>
                  <span>{{ connectionError }}</span>
                </div>

                <div v-if="!connectionSuccess && whatsappStatus.status !== 'Connected'" class="note-box warning">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
                  </svg>
                  <span>{{ translations[currentLang].almostThere }}</span>
                </div>

                <div v-if="!isWhatsAppConnected" class="note-box info" style="margin-top: 16px;">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  <span>{{ currentLang === 'en' ? 'You must successfully connect WhatsApp before completing the tutorial.' : 'يجب عليك توصيل الواتساب بنجاح قبل إكمال البرنامج التعليمي.' }}</span>
                </div>
              </div>

              <!-- Completion Step -->
              <div v-else-if="currentStepData.type === 'complete'" key="complete" class="step-card success-card">
                <div class="success-icon">🎉</div>
                <h2 class="success-title">{{ translations[currentLang].congratulations }}</h2>
                <p class="success-desc">{{ translations[currentLang].congratsText }}</p>
                <p class="success-subdesc">{{ translations[currentLang].congratsSubText }}</p>
                <ul class="success-list">
                  <li v-for="(item, index) in translations[currentLang].congratsList" :key="index">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                    </svg>
                    <span>{{ item }}</span>
                  </li>
                </ul>
                <p class="final-message">{{ translations[currentLang].finalMessage }}</p>
              </div>
            </transition>
          </div>

          <!-- Navigation Buttons -->
          <div class="navigation-buttons">
            <button
              @click="previousStep"
              :disabled="isFirstStep"
              class="nav-btn prev-btn"
              :class="{ disabled: isFirstStep }"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z"/>
              </svg>
              <span>Previous</span>
            </button>

            <button
              @click="nextStep"
              :disabled="!canGoNext"
              class="nav-btn next-btn"
              :class="{ disabled: !canGoNext }"
            >
              <span>{{ isLastStep ? 'Finish' : 'Next' }}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"/>
              </svg>
            </button>
          </div>
        </div>
      </main>
    </div>

    <!-- Lightbox Modal -->
    <div v-if="lightboxOpen" class="lightbox-overlay" @click="closeLightbox">
      <div class="lightbox-container" @click.stop>
        <button class="lightbox-close" @click="closeLightbox" title="Close (Esc)">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
          </svg>
        </button>
        <div class="lightbox-actions">
          <a :href="lightboxImage" :download="lightboxAlt + '.png'" class="lightbox-download" title="Download">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
            </svg>
            Download
          </a>
        </div>
        <img :src="lightboxImage" :alt="lightboxAlt" class="lightbox-image" />
        <p class="lightbox-caption">{{ lightboxAlt }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { AuthService } from '@/services/auth'
import { WhatsAppService } from '@/services/whatsapp'

export default {
  name: 'WhatsAppTutorialView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      currentStep: 0,
      completedSteps: [],
      currentLang: 'en',
      lightboxOpen: false,
      lightboxImage: '',
      lightboxAlt: '',
      allSteps: [
        // Introduction
        { id: 'intro', type: 'intro', mainStep: 0 },
        // Step 1 - Create Meta App (9 sub-steps)
        { id: '1.1', type: 'substep', mainStep: 1, substep: 1 },
        { id: '1.2', type: 'substep', mainStep: 1, substep: 2 },
        { id: '1.3', type: 'substep', mainStep: 1, substep: 3 },
        { id: '1.4', type: 'substep', mainStep: 1, substep: 4 },
        { id: '1.5', type: 'substep', mainStep: 1, substep: 5 },
        { id: '1.6', type: 'substep', mainStep: 1, substep: 6 },
        { id: '1.7', type: 'substep', mainStep: 1, substep: 7 },
        { id: '1.8', type: 'substep', mainStep: 1, substep: 8 },
        { id: '1.9', type: 'substep', mainStep: 1, substep: 9 },
        // Step 2 - Get Phone Number ID (1 sub-step)
        { id: '2.1', type: 'substep', mainStep: 2, substep: 1 },
        // Step 3 - Generate Token (3 sub-steps)
        { id: '3.1', type: 'substep', mainStep: 3, substep: 1 },
        { id: '3.2', type: 'substep', mainStep: 3, substep: 2 },
        { id: '3.3', type: 'substep', mainStep: 3, substep: 3 },
        // Step 4 - Connect to QUALI-D (2 sub-steps)
        { id: '4.1', type: 'substep', mainStep: 4, substep: 1 },
        { id: '4.2', type: 'substep', mainStep: 4, substep: 2 },
        // Completion
        { id: 'complete', type: 'complete', mainStep: 5 }
      ],
      // WhatsApp Settings Form
      whatsappSettings: {
        whatsappToken: '',
        whatsappPhoneId: ''
      },
      showToken: false,
      settingsLoading: false,
      settingsSaved: false,
      credentialsValidated: false, // Track if credentials were ever saved successfully
      settingsError: '',
      // Webhook
      webhookUrl: '',
      webhookLoading: false,
      webhookCopied: '',
      // WhatsApp Connection Status
      whatsappStatus: {
        status: 'Unknown',
        uniqueSessionId: null
      },
      connectionLoading: false,
      connectionSuccess: false,
      connectionError: '',
      translations: {
        en: {
          mainSteps: 'Main Steps',
          minutesToComplete: 'Minutes',
          freeToUse: 'Free',
          yourMetaApp: 'Your Meta App',
          yourWhatsApp: 'Your WhatsApp',
          yourCustomers: 'Your Customers',
          welcome: 'Welcome to QUALI-D',
          introText: 'This tutorial will guide you through integrating <strong>your own WhatsApp Business API</strong> with QUALI-D\'s AI chatbot platform.',
          whatYouWillAccomplish: 'What you\'ll accomplish:',
          accomplishList: [
            'Create and configure your Meta Business App',
            'Connect your WhatsApp Business phone number',
            'Set up your product/service knowledge base',
            'Configure appointment scheduling (optional)'
          ],
          important: 'Important',
          importantNote: 'You will create a Meta app under YOUR business name using YOUR WhatsApp number. QUALI-D powers the chatbot, but you own the WhatsApp Business account.',
          howThisWorks: 'How This Works',
          step1Title: 'Create Your Meta Business App',
          step1_1: 'Access Meta for Developers',
          step1_1Content: 'Navigate to Meta for Developers and sign in with your Facebook account.',
          step1_1Note: 'You must have a Facebook Business Manager account. You\'ll be prompted to create one if needed.',
          step1_2: 'Create a New App',
          step1_2List: [
            'Click <strong>"My Apps"</strong> in the top right',
            'Click <strong>"Create App"</strong>',
            'Select <strong>"Business"</strong> as app type',
            'Click <strong>"Next"</strong>'
          ],
          step1_3: 'Configure App Details',
          step1_3Content: 'Fill in your application information:',
          step1_3List: [
            '<strong>App Name:</strong> Use YOUR business name',
            '<strong>Contact Email:</strong> Your business email',
            'Click <strong>"Next"</strong> to proceed'
          ],
          step1_3Note: 'Use your actual business name. This app belongs to you and connects to QUALI-D.',
          step1_4: 'Select Use Case',
          step1_4Content: 'Choose how you plan to use your Meta app:',
          step1_4List: [
            'Select <strong>"Connect with customers through WhatsApp"</strong>',
            'Click <strong>"Next"</strong>'
          ],
          step1_4Note: 'This tells Meta you\'re setting up a WhatsApp Business integration.',
          step1_5: 'Choose Business Portfolio',
          step1_5Content: 'Select or create a Business Portfolio:',
          step1_5List: [
            'Choose an <strong>existing Business Portfolio</strong> if you have one',
            'Or click <strong>"Create a new Business Portfolio"</strong> (<a href="https://business.facebook.com/latest/settings/portfolios" target="_blank" rel="noopener">Learn how to create one</a>)',
            'Click <strong>"Next"</strong>'
          ],
          step1_5Note: 'A Business Portfolio organizes your business assets like apps, ad accounts, and pages.',
          step1_6: 'Review and Confirm',
          step1_6Content: 'Review your setup on the Overview page:',
          step1_6List: [
            'Review all the information you entered',
            'Make sure everything is correct',
            'Click <strong>"Go To Dashboard"</strong> to complete the setup'
          ],
          step1_6Note: 'This will take you to your app dashboard where you can add WhatsApp.',
          step1_7: 'Customize WhatsApp Use Case',
          step1_7Content: 'From your app dashboard:',
          step1_7List: [
            'Look for <strong>"App customization and requirements"</strong> section',
            'Click <strong>"Customize the Connect with customers through WhatsApp use case"</strong>',
            'You\'ll be taken to the use case customization page'
          ],
          step1_7Note: 'This is where you configure WhatsApp Business Platform settings for your app.',
          step1_8: 'Accept Terms and Select Business Portfolio',
          step1_8Content: 'Configure WhatsApp Business Platform settings:',
          step1_8List: [
            'Read and click <strong>"Accept Terms of Service"</strong> for WhatsApp Business Platform',
            'Under <strong>"WhatsApp Business Platform"</strong>, your Business Portfolio should be pre-selected',
            'Review the note about receiving a test phone number (max 5 numbers)',
            'Click <strong>"Continue"</strong> at the bottom'
          ],
          step1_8Note: 'You agree to Facebook Terms for WhatsApp Business and Meta Hosting Terms for Cloud API.',
          step1_9: 'Start Using the API',
          step1_9Content: 'Welcome to WhatsApp Business Platform setup:',
          step1_9List: [
            'You\'ll see <strong>"Welcome to the WhatsApp Business Platform"</strong>',
            'Read about sending and receiving messages using Cloud API',
            'Under <strong>"API Setup"</strong>, click <strong>"Start using the API"</strong>',
            'This takes you to the API configuration page'
          ],
          step1_9Note: 'Meta hosts the WhatsApp Business API client on cloud-based servers for you.',
          step2Title: 'Get Phone Number ID',
          step2_1: 'Copy Your Phone Number ID',
          step2_1Content: 'On the API Setup page, you\'ll see your test configuration. You\'ll need to enter this Phone Number ID in your QUALI-D dashboard:',
          step2_1List: [
            'Scroll to <strong>"Step 1: Select phone numbers"</strong>',
            'Under the <strong>"From"</strong> section, you\'ll see a test phone number',
            'Find <strong>"Phone number ID:"</strong> with a numeric ID below it',
            'Click the copy button next to the <strong>Phone Number ID</strong>',
            'Save this ID - <strong>you\'ll enter it in the QUALI-D dashboard</strong> under WhatsApp Settings',
            'Also note the <strong>"WhatsApp Business Account ID"</strong> - you\'ll need this for token generation'
          ],
          step2_1Warning: 'The Phone Number ID is different from your actual phone number. This is a unique identifier for API calls.',
          step2_1Note: 'Test phone numbers let you send free messages for 90 days to up to 5 numbers.',
          step3Title: 'Generate Access Token',
          step3_1: 'Create System User',
          step3_1Content: 'Create a System User for permanent token:',
          step3_1List: [
            'Navigate to <strong>"Business Settings"</strong>',
            'Select <strong>"System Users"</strong>',
            'Click <strong>"Add"</strong>',
            'Name it (e.g., "WhatsApp Integration")',
            'Set role to <strong>"Admin"</strong>',
            'Click <strong>"Create"</strong>'
          ],
          step3_2: 'Assign Assets',
          step3_2List: [
            'Click the new system user',
            'Click <strong>"Add Assets"</strong>',
            'Select <strong>"Apps"</strong>',
            'Choose your app',
            'Enable <strong>"Manage App"</strong>',
            'Click <strong>"Save"</strong>'
          ],
          step3_3: 'Generate Token',
          step3_3List: [
            'Click <strong>"Generate New Token"</strong>',
            'Select your app',
            'Select permissions: <code>whatsapp_business_management</code>, <code>whatsapp_business_messaging</code>, <code>whatsapp_business_templates</code>, <code>business_management</code>',
            'Set expiration to <strong>"Never"</strong>',
            'Click <strong>"Generate"</strong>',
            '<strong>Copy immediately!</strong> You won\'t see it again',
            '<strong>Enter this token in the QUALI-D dashboard</strong> under WhatsApp Settings along with your Phone Number ID'
          ],
          securityWarning: 'Security Warning',
          step3_3Warning: 'This token provides full API access. Never share publicly or commit to version control. Only enter it in your QUALI-D dashboard.',
          step4Title: 'Connect to QUALI-D',
          step4_1: 'Save Your Credentials',
          step4_1Content: 'Now that you have your Access Token and Phone Number ID, let\'s save them to QUALI-D:',
          step4_1Note: 'These credentials will be securely stored and used to connect your WhatsApp to QUALI-D.',
          webhookConfigTitle: 'Webhook Configuration',
          webhookConfigDesc: 'Copy these values and configure them in your Meta WhatsApp app settings.',
          webhookUrlLabel: 'Webhook URL',
          verifyTokenLabel: 'Verify Token',
          copyWebhookUrl: 'Copy Webhook URL',
          copyVerifyToken: 'Copy Verify Token',
          loading: 'Loading...',
          webhookCopiedSuccess: 'Copied to clipboard!',
          whatsappTokenLabel: 'WhatsApp Access Token',
          whatsappTokenPlaceholder: 'Paste your access token here',
          whatsappPhoneIdLabel: 'WhatsApp Phone Number ID',
          whatsappPhoneIdPlaceholder: 'Paste your phone number ID here',
          saveCredentialsBtn: 'Save Credentials',
          savingCredentials: 'Saving...',
          credentialsSaved: 'Credentials saved successfully!',
          credentialsSaveError: 'Failed to save credentials. Please try again.',
          step4_2: 'Initialize WhatsApp Connection',
          step4_2Content: 'Your credentials are saved! Now let\'s establish the connection between your WhatsApp and QUALI-D:',
          step4_2Instructions: 'Click the INITIALIZE button below to start the connection process. This will:',
          step4_2List: [
            'Connect your WhatsApp Business account',
            'Enable QUALI-D AI to manage conversations',
            'Activate the chatbot on your WhatsApp number'
          ],
          currentStatus: 'Current Status',
          initializeBtn: 'Initialize Connection',
          initializing: 'Initializing...',
          recheckBtn: 'Recheck Status',
          rechecking: 'Checking...',
          connectionSuccess: 'Connection established successfully! Your WhatsApp is now connected to QUALI-D.',
          connectionPending: 'Connection in progress. Please wait...',
          connectionFailed: 'Connection failed. Please check your credentials and try again.',
          almostThere: 'Almost there! Just initialize the connection to complete setup.',
          congratulations: 'Congratulations!',
          congratsText: 'You\'ve successfully integrated WhatsApp Business API with QUALI-D!',
          congratsSubText: 'Your AI-powered chatbot is ready to:',
          congratsList: [
            'Answer customer questions 24/7',
            'Provide product/service information',
            'Handle appointment bookings',
            'Engage customers instantly'
          ],
          finalMessage: 'Start promoting your WhatsApp number and watch engagement soar! 🚀'
        },
        ar: {
          mainSteps: 'الخطوات الرئيسية',
          minutesToComplete: 'دقيقة',
          freeToUse: 'مجاني',
          yourMetaApp: 'تطبيق Meta الخاص بك',
          yourWhatsApp: 'واتساب الخاص بك',
          yourCustomers: 'عملاؤك',
          welcome: 'مرحباً بك في QUALI-D',
          introText: 'سيرشدك هذا الدليل لدمج <strong>WhatsApp Business API الخاص بك</strong> مع منصة QUALI-D.',
          whatYouWillAccomplish: 'ما ستحققه:',
          accomplishList: [
            'إنشاء وتهيئة تطبيق Meta Business',
            'ربط رقم WhatsApp Business الخاص بك',
            'إعداد قاعدة معرفة المنتجات/الخدمات',
            'تكوين جدولة المواعيد (اختياري)'
          ],
          important: 'مهم',
          importantNote: 'ستنشئ تطبيق Meta باسم عملك وبرقم WhatsApp الخاص بك. QUALI-D تشغل الروبوت، لكنك تملك حساب WhatsApp.',
          howThisWorks: 'كيف يعمل',
          step1Title: 'إنشاء تطبيق Meta',
          step1_1: 'الوصول إلى Meta للمطورين',
          step1_1Content: 'انتقل إلى Meta للمطورين وسجل الدخول.',
          step1_1Note: 'يجب أن يكون لديك حساب Business Manager.',
          step1_2: 'إنشاء تطبيق جديد',
          step1_2List: [
            'انقر <strong>"تطبيقاتي"</strong>',
            'انقر <strong>"إنشاء تطبيق"</strong>',
            'اختر <strong>"الأعمال"</strong>',
            'انقر <strong>"التالي"</strong>'
          ],
          step1_3: 'تكوين التطبيق',
          step1_3Content: 'املأ معلومات التطبيق:',
          step1_3List: [
            '<strong>اسم التطبيق:</strong> اسم عملك',
            '<strong>البريد:</strong> بريدك للأعمال',
            'انقر <strong>"التالي"</strong> للمتابعة'
          ],
          step1_3Note: 'استخدم اسم عملك الحقيقي.',
          step1_4: 'اختيار حالة الاستخدام',
          step1_4Content: 'اختر كيف تخطط لاستخدام تطبيق Meta:',
          step1_4List: [
            'اختر <strong>"التواصل مع العملاء عبر واتساب"</strong>',
            'انقر <strong>"التالي"</strong>'
          ],
          step1_4Note: 'هذا يخبر Meta أنك تقوم بإعداد تكامل WhatsApp Business.',
          step1_5: 'اختيار محفظة الأعمال',
          step1_5Content: 'اختر أو أنشئ محفظة أعمال:',
          step1_5List: [
            'اختر <strong>محفظة أعمال موجودة</strong> إذا كان لديك واحدة',
            'أو انقر <strong>"إنشاء محفظة أعمال جديدة"</strong> (<a href="https://business.facebook.com/latest/settings/portfolios" target="_blank" rel="noopener">تعلم كيفية إنشاء واحدة</a>)',
            'انقر <strong>"التالي"</strong>'
          ],
          step1_5Note: 'تنظم محفظة الأعمال أصول عملك مثل التطبيقات وحسابات الإعلانات والصفحات.',
          step1_6: 'المراجعة والتأكيد',
          step1_6Content: 'راجع إعدادك في صفحة النظرة العامة:',
          step1_6List: [
            'راجع جميع المعلومات التي أدخلتها',
            'تأكد من صحة كل شيء',
            'انقر <strong>"الذهاب إلى لوحة التحكم"</strong> لإكمال الإعداد'
          ],
          step1_6Note: 'سينقلك هذا إلى لوحة تحكم تطبيقك حيث يمكنك إضافة WhatsApp.',
          step1_7: 'تخصيص حالة استخدام WhatsApp',
          step1_7Content: 'من لوحة تحكم تطبيقك:',
          step1_7List: [
            'ابحث عن قسم <strong>"App customization and requirements"</strong>',
            'انقر <strong>"تخصيص حالة الاستخدام للتواصل مع العملاء عبر واتساب"</strong>',
            'سيتم نقلك إلى صفحة تخصيص حالة الاستخدام'
          ],
          step1_7Note: 'هنا ستقوم بتكوين إعدادات WhatsApp Business Platform لتطبيقك.',
          step1_8: 'قبول الشروط واختيار محفظة الأعمال',
          step1_8Content: 'تكوين إعدادات WhatsApp Business Platform:',
          step1_8List: [
            'اقرأ وانقر <strong>"قبول شروط الخدمة"</strong> لـ WhatsApp Business Platform',
            'تحت <strong>"WhatsApp Business Platform"</strong>، يجب أن تكون محفظة أعمالك محددة مسبقاً',
            'راجع الملاحظة حول الحصول على رقم اختبار (بحد أقصى 5 أرقام)',
            'انقر <strong>"متابعة"</strong> في الأسفل'
          ],
          step1_8Note: 'أنت توافق على شروط Facebook لـ WhatsApp Business وشروط استضافة Meta للـ Cloud API.',
          step1_9: 'بدء استخدام الـ API',
          step1_9Content: 'مرحباً بك في إعداد WhatsApp Business Platform:',
          step1_9List: [
            'سترى <strong>"مرحباً بك في WhatsApp Business Platform"</strong>',
            'اقرأ عن إرسال واستقبال الرسائل باستخدام Cloud API',
            'تحت <strong>"API Setup"</strong>، انقر <strong>"بدء استخدام الـ API"</strong>',
            'سينقلك هذا إلى صفحة تكوين الـ API'
          ],
          step1_9Note: 'Meta تستضيف عميل WhatsApp Business API على خوادم سحابية من أجلك.',
          step2Title: 'الحصول على معرف رقم الهاتف',
          step2_1: 'نسخ معرف رقم الهاتف',
          step2_1Content: 'في صفحة API Setup، سترى تكوين الاختبار الخاص بك. ستحتاج إلى إدخال معرف رقم الهاتف هذا في لوحة تحكم QUALI-D:',
          step2_1List: [
            'انتقل إلى <strong>"Step 1: Select phone numbers"</strong>',
            'تحت قسم <strong>"From"</strong>، سترى رقم هاتف اختبار',
            'ابحث عن <strong>"Phone number ID:"</strong> مع معرف رقمي أسفله',
            'انقر زر النسخ بجوار <strong>معرف رقم الهاتف</strong>',
            'احفظ هذا المعرف - <strong>ستقوم بإدخاله في لوحة تحكم QUALI-D</strong> ضمن إعدادات WhatsApp',
            'لاحظ أيضاً <strong>"WhatsApp Business Account ID"</strong> - ستحتاجه لإنشاء الرمز'
          ],
          step2_1Warning: 'معرف رقم الهاتف مختلف عن رقم هاتفك الفعلي. هذا معرف فريد لاستدعاءات API.',
          step2_1Note: 'أرقام الاختبار تتيح لك إرسال رسائل مجانية لمدة 90 يوماً إلى 5 أرقام كحد أقصى.',
          step3Title: 'إنشاء رمز الوصول',
          step3_1: 'إنشاء مستخدم نظام',
          step3_1Content: 'أنشئ مستخدم نظام للحصول على رمز دائم:',
          step3_1List: [
            'اذهب إلى <strong>"إعدادات الأعمال"</strong> (Business Settings)',
            'اختر <strong>"مستخدمو النظام"</strong> (System Users)',
            'انقر <strong>"إضافة"</strong> (Add)',
            'أعطه اسماً (مثال: "WhatsApp Integration")',
            'اضبط الدور <strong>"Admin"</strong>',
            'انقر <strong>"إنشاء"</strong> (Create)'
          ],
          step3_2: 'تعيين الأصول',
          step3_2List: [
            'انقر مستخدم النظام الجديد',
            'انقر <strong>"Add Assets"</strong>',
            'اختر <strong>"Apps"</strong>',
            'اختر تطبيقك',
            'فعّل <strong>"Manage App"</strong>',
            'انقر <strong>"حفظ"</strong>'
          ],
          step3_3: 'إنشاء الرمز',
          step3_3List: [
            'انقر <strong>"Generate New Token"</strong>',
            'اختر تطبيقك',
            'اختر الأذونات: <code>whatsapp_business_management</code>, <code>whatsapp_business_messaging</code>, <code>whatsapp_business_templates</code>, <code>business_management</code>',
            'اضبط انتهاء الصلاحية <strong>"Never"</strong>',
            'انقر <strong>"Generate"</strong>',
            '<strong>انسخ فوراً!</strong> لن تراه مرة أخرى',
            '<strong>أدخل هذا الرمز في لوحة تحكم QUALI-D</strong> ضمن إعدادات WhatsApp مع معرف رقم الهاتف'
          ],
          securityWarning: 'تحذير أمني',
          step3_3Warning: 'هذا الرمز يوفر وصولاً كاملاً. لا تشاركه علناً. أدخله فقط في لوحة تحكم QUALI-D.',
          step4Title: 'الاتصال بـ QUALI-D',
          step4_1: 'احفظ بيانات الاعتماد',
          step4_1Content: 'الآن بعد أن حصلت على رمز الوصول ومعرف الرقم، دعنا نحفظهم في QUALI-D:',
          step4_1Note: 'سيتم تخزين بيانات الاعتماد بشكل آمن واستخدامها لربط واتساب بـ QUALI-D.',
          webhookConfigTitle: 'إعدادات Webhook',
          webhookConfigDesc: 'انسخ هذه القيم وأعدها في إعدادات تطبيق ميتا واتساب الخاص بك.',
          webhookUrlLabel: 'رابط Webhook',
          verifyTokenLabel: 'رمز التحقق',
          copyWebhookUrl: 'نسخ رابط Webhook',
          copyVerifyToken: 'نسخ رمز التحقق',
          loading: 'جاري التحميل...',
          webhookCopiedSuccess: 'تم النسخ إلى الحافظة!',
          whatsappTokenLabel: 'رمز وصول WhatsApp',
          whatsappTokenPlaceholder: 'الصق رمز الوصول هنا',
          whatsappPhoneIdLabel: 'معرف رقم WhatsApp',
          whatsappPhoneIdPlaceholder: 'الصق معرف الرقم هنا',
          saveCredentialsBtn: 'حفظ البيانات',
          savingCredentials: 'جاري الحفظ...',
          credentialsSaved: 'تم حفظ البيانات بنجاح!',
          credentialsSaveError: 'فشل حفظ البيانات. يرجى المحاولة مرة أخرى.',
          step4_2: 'تهيئة اتصال WhatsApp',
          step4_2Content: 'تم حفظ بياناتك! الآن دعنا ننشئ الاتصال بين واتساب و QUALI-D:',
          step4_2Instructions: 'انقر على زر التهيئة أدناه لبدء عملية الاتصال. سيقوم هذا بـ:',
          step4_2List: [
            'ربط حساب WhatsApp Business الخاص بك',
            'تفعيل QUALI-D AI لإدارة المحادثات',
            'تنشيط الروبوت على رقم واتساب الخاص بك'
          ],
          currentStatus: 'الحالة الحالية',
          initializeBtn: 'تهيئة الاتصال',
          initializing: 'جاري التهيئة...',
          recheckBtn: 'إعادة الفحص',
          rechecking: 'جاري الفحص...',
          connectionSuccess: 'تم إنشاء الاتصال بنجاح! واتساب الخاص بك متصل الآن بـ QUALI-D.',
          connectionPending: 'الاتصال قيد التقدم. يرجى الانتظار...',
          connectionFailed: 'فشل الاتصال. يرجى التحقق من بياناتك والمحاولة مرة أخرى.',
          almostThere: 'كدت تنتهي! فقط قم بتهيئة الاتصال لإكمال الإعداد.',
          congratulations: 'تهانينا!',
          congratsText: 'نجحت في دمج WhatsApp مع QUALI-D!',
          congratsSubText: 'روبوتك جاهز لـ:',
          congratsList: [
            'الإجابة على الأسئلة 24/7',
            'تقديم معلومات المنتجات',
            'حجز المواعيد',
            'التفاعل الفوري'
          ],
          finalMessage: 'ابدأ الترويج لرقمك وشاهد التفاعل يرتفع! 🚀'
        }
      }
    }
  },
  computed: {
    progress () {
      return Math.round((this.currentStep / (this.allSteps.length - 1)) * 100)
    },
    currentStepData () {
      return this.allSteps[this.currentStep]
    },
    isFirstStep () {
      return this.currentStep === 0
    },
    isLastStep () {
      return this.currentStep === this.allSteps.length - 1
    },
    isWhatsAppConnected () {
      return this.whatsappStatus.status === 'Connected'
    },
    canGoNext () {
      // If we're on Step 4.1 (Save Credentials), only allow next if credentials are saved
      if (this.currentStepData.id === '4.1') {
        // Must have filled both fields AND saved successfully at least once
        const hasCredentials = this.whatsappSettings.whatsappToken && this.whatsappSettings.whatsappPhoneId
        if (!hasCredentials || !this.credentialsValidated) {
          return false
        }
      }
      // If we're on Step 4.2 (Initialize Connection), only allow next if connected
      if (this.currentStepData.id === '4.2' && !this.isWhatsAppConnected) {
        return false
      }
      return !this.isLastStep
    },
    canGoPrevious () {
      return !this.isFirstStep
    }
  },
  mounted () {
    // Load existing credentials on component mount
    this.loadWhatsAppCredentials()
    // Load webhook URL on component mount
    this.loadWebhookUrl()
  },
  methods: {
    openLightbox (imageSrc, imageAlt) {
      this.lightboxImage = imageSrc
      this.lightboxAlt = imageAlt
      this.lightboxOpen = true
      document.body.style.overflow = 'hidden'
      // Add ESC key listener
      document.addEventListener('keydown', this.handleEscKey)
    },
    closeLightbox () {
      this.lightboxOpen = false
      this.lightboxImage = ''
      this.lightboxAlt = ''
      document.body.style.overflow = ''
      // Remove ESC key listener
      document.removeEventListener('keydown', this.handleEscKey)
    },
    handleEscKey (event) {
      if (event.key === 'Escape') {
        this.closeLightbox()
      }
    },
    switchLanguage (lang) {
      this.currentLang = lang
    },
    nextStep () {
      if (this.canGoNext) {
        // Mark current step as completed
        if (!this.completedSteps.includes(this.currentStep)) {
          this.completedSteps.push(this.currentStep)
        }
        this.currentStep++
        this.scrollToTop()
        this.onStepChange()
      }
    },
    previousStep () {
      if (this.canGoPrevious) {
        this.currentStep--
        this.scrollToTop()
        this.onStepChange()
      }
    },
    goToStep (stepIndex) {
      if (stepIndex >= 0 && stepIndex < this.allSteps.length) {
        this.currentStep = stepIndex
        this.scrollToTop()
        this.onStepChange()
      }
    },
    onStepChange () {
      // Load credentials when entering Step 4.1
      if (this.currentStepData.id === '4.1') {
        this.loadWhatsAppCredentials()
      }
      // Load connection status when entering Step 4.2
      if (this.currentStepData.id === '4.2') {
        this.loadWhatsAppStatus()
      }
    },
    scrollToTop () {
      const pageContent = document.querySelector('.page-content')
      if (pageContent) {
        pageContent.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },

    // WhatsApp Settings Methods
    async saveWhatsAppSettings () {
      try {
        this.settingsLoading = true
        this.settingsError = ''
        this.settingsSaved = false

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('No authentication token found')
        }

        const response = await fetch('https://dashboard.quali-d.com/api/meta_set_whatsapp_keys.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            whatsapp_token: this.whatsappSettings.whatsappToken,
            whatsapp_phone_id: this.whatsappSettings.whatsappPhoneId,
            token: token
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.success === true || data.status === 'success') {
          this.settingsSaved = true
          this.credentialsValidated = true // Mark credentials as validated
          // Load webhook URL after successful save
          this.loadWebhookUrl()
          setTimeout(() => {
            this.settingsSaved = false
          }, 3000)
        } else {
          throw new Error(data.message || 'Failed to save settings')
        }
      } catch (error) {
        this.settingsError = error.message || this.translations[this.currentLang].credentialsSaveError
      } finally {
        this.settingsLoading = false
      }
    },

    // Load existing WhatsApp credentials
    async loadWhatsAppCredentials () {
      try {
        const token = AuthService.getAuthToken()
        if (!token) return

        const response = await fetch('https://api.quali-d.com/whatsapp-credentials', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        if (!response.ok) return

        const data = await response.json()

        // Only load if credentials exist
        if (data.success && data.credentials) {
          this.whatsappSettings = {
            whatsappToken: data.credentials.whatsapp_token || '',
            whatsappPhoneId: data.credentials.whatsapp_phone_id || ''
          }
        }
      } catch (error) {
        // Silently fail - user can still enter credentials manually
        console.error('Error loading WhatsApp credentials:', error)
      }
    },

    // WhatsApp Connection Methods
    async loadWhatsAppStatus () {
      try {
        const result = await WhatsAppService.getConnectionStatus()
        this.whatsappStatus = {
          status: result.status || 'Unknown',
          uniqueSessionId: result.uniqueSessionId || null
        }
      } catch (error) {
        this.whatsappStatus = {
          status: 'Error',
          uniqueSessionId: null
        }
      }
    },

    async initializeWhatsAppConnection () {
      if (this.connectionLoading) return

      try {
        this.connectionLoading = true
        this.connectionError = ''
        this.connectionSuccess = false

        const result = await WhatsAppService.initializeConnection()

        if (result.success) {
          // Update status to connecting while initialization is processing
          this.whatsappStatus.status = 'Connecting'

          // Wait a moment then recheck status to see if it connected
          setTimeout(async () => {
            await this.recheckWhatsAppConnection()
          }, 3000)
        } else {
          throw new Error(result.error || 'Failed to initialize connection')
        }
      } catch (error) {
        this.connectionError = error.message || this.translations[this.currentLang].connectionFailed
      } finally {
        this.connectionLoading = false
      }
    },

    async recheckWhatsAppConnection () {
      if (this.connectionLoading) return

      try {
        this.connectionLoading = true

        const result = await WhatsAppService.refreshConnectionStatus()

        this.whatsappStatus = {
          status: result.status || 'Unknown',
          uniqueSessionId: result.uniqueSessionId || null
        }

        if (result.status === 'Connected') {
          this.connectionSuccess = true
          this.connectionError = ''
        }
      } catch (error) {
        this.connectionError = error.message
      } finally {
        this.connectionLoading = false
      }
    },

    // Webhook Methods
    async loadWebhookUrl () {
      try {
        this.webhookLoading = true

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('No authentication token found')
        }

        const response = await fetch('https://api.quali-d.com/get-whatsapp-inst-wh', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to get webhook URL')
        }

        const data = await response.json()

        if (!data.success || !data.webhook_url) {
          throw new Error(data.message || 'Invalid webhook response')
        }

        this.webhookUrl = data.webhook_url
      } catch (error) {
        this.webhookUrl = 'Error loading webhook URL'
        console.error('Error loading webhook URL:', error)
      } finally {
        this.webhookLoading = false
      }
    },

    async copyWebhookUrl () {
      try {
        if (!this.webhookUrl || this.webhookUrl === 'Error loading webhook URL') {
          await this.loadWebhookUrl()
        }

        if (this.webhookUrl && this.webhookUrl !== 'Error loading webhook URL') {
          await navigator.clipboard.writeText(this.webhookUrl)
          this.webhookCopied = this.translations[this.currentLang].webhookCopiedSuccess

          setTimeout(() => {
            this.webhookCopied = ''
          }, 3000)
        }
      } catch (error) {
        this.webhookCopied = error.message || 'Failed to copy webhook URL'
      }
    },

    async copyVerifyToken () {
      try {
        const verifyToken = 'secret@2024'
        await navigator.clipboard.writeText(verifyToken)
        this.webhookCopied = this.translations[this.currentLang].webhookCopiedSuccess

        setTimeout(() => {
          this.webhookCopied = ''
        }, 3000)
      } catch (error) {
        this.webhookCopied = 'Failed to copy verify token'
      }
    }
  }
}
</script>

<style scoped>
.tutorial-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
  overflow-y: auto;
}

.tutorial-container {
  max-width: 900px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 24px;
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  transition: color 0.3s ease;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

/* Language Switcher */
.language-switcher {
  display: flex;
  gap: 8px;
  background: var(--bg-primary);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
}

.lang-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.lang-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.lang-btn.active {
  background: #3b82f6;
  color: white;
}

/* Progress Section */
.progress-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-label {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.progress-percentage {
  font-size: 24px;
  font-weight: 700;
  color: #3b82f6;
}

.step-counter {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.progress-bar-container {
  position: relative;
  height: 12px;
  background: var(--bg-secondary);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 16px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%);
  border-radius: 6px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.progress-bar-glow {
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3));
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-40px);
  }
  100% {
    transform: translateX(40px);
  }
}

.progress-milestones {
  display: flex;
  justify-content: space-between;
  gap: 4px;
}

.milestone {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.milestone:hover .milestone-dot {
  transform: scale(1.2);
}

.milestone-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--bg-secondary);
  border: 2px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: var(--text-secondary);
  font-weight: 600;
  transition: all 0.3s;
}

.milestone.active .milestone-dot {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
  transform: scale(1.1);
}

.milestone.completed .milestone-dot {
  background: #10b981;
  border-color: #10b981;
}

.milestone-label {
  font-size: 10px;
  color: var(--text-secondary);
  font-weight: 500;
  text-align: center;
}

/* Stats Overview */
.stats-overview {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
}

.stat-icon {
  font-size: 28px;
}

.stat-number {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.stat-label {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
  transition: color 0.3s ease;
}

/* Step Content Wrapper */
.step-content-wrapper {
  margin-bottom: 24px;
  min-height: 400px;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Step Card */
.step-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 32px;
  transition: all 0.3s ease;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.step-badge {
  display: inline-block;
  padding: 6px 12px;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step-header-inline {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.step-number {
  width: 48px;
  height: 48px;
  background: #3b82f6;
  color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}

.step-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.card-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  transition: color 0.3s ease;
}

.step-desc,
.intro-text {
  font-size: 16px;
  line-height: 1.6;
  color: var(--text-primary);
  margin-bottom: 20px;
  transition: color 0.3s ease;
}

.accomplishments h3,
.section-subtitle {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 24px 0 16px 0;
  transition: color 0.3s ease;
}

.checklist {
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
}

.checklist li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  font-size: 15px;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.check-icon {
  color: #10b981;
  flex-shrink: 0;
}

.instruction-list {
  margin: 0 0 20px 0;
  padding-left: 24px;
}

.instruction-list li {
  margin-bottom: 12px;
  font-size: 15px;
  line-height: 1.7;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.instruction-list li:last-child {
  margin-bottom: 0;
}

/* Info Note */
.info-note {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: var(--bg-secondary);
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.info-note svg {
  color: #3b82f6;
  flex-shrink: 0;
  margin-top: 2px;
}

/* URL Box */
.url-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  margin: 16px 0;
  transition: all 0.3s ease;
}

.url-box svg {
  color: #3b82f6;
  flex-shrink: 0;
}

.url-box a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
}

.url-box a:hover {
  text-decoration: underline;
}

/* Note Boxes */
.note-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  margin-top: 16px;
  transition: all 0.3s ease;
}

.note-box svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.note-box.info {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: var(--text-primary);
}

.note-box.info svg {
  color: #3b82f6;
}

.note-box.warning {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  color: var(--text-primary);
}

.note-box.warning svg {
  color: #f59e0b;
}

.note-box.danger {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: var(--text-primary);
}

.note-box.danger svg {
  color: #ef4444;
}

.note-box.success {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  color: var(--text-primary);
}

.note-box.success svg {
  color: #10b981;
}

/* Architecture Diagram */
.how-it-works-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-primary);
}

.architecture-diagram {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 20px;
  background: var(--bg-secondary);
  border-radius: 8px;
  overflow-x: auto;
  transition: background-color 0.3s ease;
}

.arch-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 80px;
}

.arch-icon {
  font-size: 28px;
  background: var(--bg-primary);
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid var(--border-primary);
  transition: all 0.3s ease;
}

.arch-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
  text-align: center;
  transition: color 0.3s ease;
}

.arch-arrow {
  font-size: 20px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

/* Success Card */
.success-card {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  text-align: center;
  border: none;
}

.success-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.success-title {
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin: 0 0 16px 0;
}

.success-desc {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 12px;
}

.success-subdesc {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 24px;
}

.success-list {
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
  display: inline-block;
  text-align: left;
}

.success-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  font-size: 16px;
  color: white;
}

.success-list svg {
  color: #10b981;
  flex-shrink: 0;
}

.final-message {
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin: 0;
}

/* Navigation Buttons */
.navigation-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 2px solid var(--border-primary);
}

.nav-btn:hover:not(.disabled) {
  background: var(--bg-secondary);
  border-color: #3b82f6;
  color: #3b82f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.next-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  margin-left: auto;
}

.next-btn:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.nav-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Webhook Section */
.webhook-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px dashed var(--border-primary);
}

.webhook-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  transition: color 0.3s ease;
}

.webhook-desc {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 20px 0;
  transition: color 0.3s ease;
}

.webhook-field {
  margin-bottom: 16px;
}

.webhook-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
  transition: color 0.3s ease;
}

.webhook-value-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.webhook-input {
  flex: 1;
  padding: 10px 14px;
  background: var(--bg-secondary);
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 13px;
  font-family: 'Courier New', monospace;
  color: var(--text-primary);
  transition: all 0.3s ease;
}

.webhook-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.webhook-input.loading {
  opacity: 0.6;
  font-style: italic;
}

.copy-btn {
  padding: 10px;
  background: var(--bg-primary);
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: var(--text-secondary);
}

.copy-btn:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #3b82f6;
  transform: scale(1.05);
}

.copy-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.copy-btn:active:not(:disabled) {
  transform: scale(0.95);
}

/* Tutorial Screenshot */
.tutorial-screenshot {
  position: relative;
  margin: 24px 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
  cursor: pointer;
}

.tutorial-screenshot:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.tutorial-screenshot:hover .screenshot-overlay {
  opacity: 1;
}

.tutorial-screenshot img {
  width: 100%;
  height: auto;
  display: block;
}

.screenshot-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* Lightbox Modal */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.lightbox-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lightbox-close {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lightbox-close:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.lightbox-actions {
  position: absolute;
  top: -50px;
  right: 50px;
}

.lightbox-download {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(59, 130, 246, 0.8);
  border: 2px solid rgba(59, 130, 246, 1);
  border-radius: 8px;
  color: white;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.lightbox-download:hover {
  background: rgba(59, 130, 246, 1);
  transform: translateY(-2px);
}

.lightbox-image {
  max-width: 100%;
  max-height: calc(90vh - 80px);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.lightbox-caption {
  margin-top: 16px;
  color: white;
  font-size: 14px;
  text-align: center;
  opacity: 0.8;
}

/* Screenshot Placeholder (legacy - can be removed if not used) */
.screenshot-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  margin: 20px 0;
  background: var(--bg-secondary);
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.screenshot-placeholder:hover {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

.placeholder-icon {
  color: var(--text-secondary);
  opacity: 0.5;
  margin-bottom: 12px;
  transition: all 0.3s ease;
}

.screenshot-placeholder:hover .placeholder-icon {
  color: #3b82f6;
  opacity: 0.8;
}

.placeholder-text {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  text-align: center;
  transition: color 0.3s ease;
}

.placeholder-filename {
  font-size: 13px;
  font-family: 'Courier New', monospace;
  color: var(--text-secondary);
  background: var(--bg-primary);
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
}

.screenshot-placeholder:hover .placeholder-filename {
  border-color: #3b82f6;
  color: #3b82f6;
}

/* WhatsApp Settings Form */
.whatsapp-settings-form {
  margin: 24px 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
  transition: color 0.3s ease;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
  pointer-events: none;
}

.form-input {
  width: 100%;
  padding: 12px 44px;
  background: var(--bg-secondary);
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  background: var(--bg-primary);
}

.form-input::placeholder {
  color: var(--text-secondary);
  opacity: 0.6;
}

.toggle-password-btn {
  position: absolute;
  right: 12px;
  padding: 6px;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.toggle-password-btn:hover {
  color: #3b82f6;
}

.save-credentials-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 8px;
}

.save-credentials-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.save-credentials-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.save-credentials-btn.loading {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
}

.save-credentials-btn.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

/* WhatsApp Connection Card */
.whatsapp-connection-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 20px;
  padding: 24px;
  margin: 24px 0;
  background: var(--bg-secondary);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.connection-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
}

.whatsapp-icon {
  width: 48px;
  height: 48px;
  transition: transform 0.3s;
}

.whatsapp-connection-card:hover .whatsapp-icon {
  transform: scale(1.05);
}

.status-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: transparent;
  transition: all 0.3s;
}

.status-glow.active {
  background: radial-gradient(circle, rgba(37, 211, 102, 0.3) 0%, transparent 70%);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.connection-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.status-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-label-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.status-value {
  font-size: 16px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 6px;
  transition: all 0.3s;
}

.status-value.connected {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.status-value.connecting {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
  animation: blink 1.5s infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.status-value.disconnected {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.session-info-row {
  margin-top: 4px;
}

.session-id-text {
  font-size: 12px;
  font-family: 'Courier New', monospace;
  color: var(--text-secondary);
  opacity: 0.8;
}

.connection-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.initialize-connection-btn,
.recheck-connection-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.initialize-connection-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
}

.initialize-connection-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.initialize-connection-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.initialize-connection-btn.loading {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
}

.recheck-connection-btn {
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 2px solid var(--border-primary);
}

.recheck-connection-btn:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #3b82f6;
  transform: translateY(-2px);
}

.recheck-connection-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 16px;
}

/* Mobile Responsive */
@media (max-width: 1024px) {
  .main-content {
    margin-left: 0;
  }
}

@media (max-width: 768px) {
  .page-content {
    padding: 16px;
    padding-top: 76px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .language-switcher {
    width: 100%;
  }

  .lang-btn {
    flex: 1;
  }

  .stats-overview {
    grid-template-columns: 1fr;
  }

  .progress-milestones {
    flex-wrap: wrap;
  }

  .milestone {
    min-width: 30px;
  }

  .milestone-label {
    display: none;
  }

  .architecture-diagram {
    flex-direction: column;
  }

  .arch-arrow {
    transform: rotate(90deg);
  }

  .step-card {
    padding: 20px;
  }

  .navigation-buttons {
    flex-direction: column;
  }

  .next-btn {
    margin-left: 0;
    width: 100%;
  }

  .nav-btn {
    width: 100%;
  }

  .whatsapp-connection-card {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .connection-visual {
    margin: 0 auto;
  }

  .connection-info {
    text-align: center;
  }

  .status-label-row {
    justify-content: center;
  }

  .connection-actions {
    width: 100%;
  }

  .initialize-connection-btn,
  .recheck-connection-btn {
    width: 100%;
  }
}
</style>
